import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from './theme';
import { supabase } from './supabase';
import { PinIcon } from './Icons';
import { feedbackUrl } from './feedbackLink';


// AppNav: the single top bar for the Dashboard, Deal, Price Watch and Trip
// History pages (Oct 10, per Ali), so every button is reachable from every
// page and the bar can't drift between screens. Same navy bar, outlined pill
// buttons, yellow Pin a Trip.
//
// Props:
//   current       'dashboard' | 'deal' | 'watch' | 'history' | 'payments' | 'pin' | 'account'. Hides the
//                 "Back to Dashboard" pill on the Dashboard and highlights
//                 the Trip History pill on its own page.
//   trialChip     Dashboard-only "Try Price Watch free" pill.
//   onBuyPins     Adds "Buy more pins" to the Account menu (Dashboard only,
//                 since it owns the purchase modal).
//   adminItems    Extra Account menu items (founder-only utility).
//   onSignOut     Defaults to the same sign-out the Dashboard uses.
const pill = { cursor: 'pointer', color: '#fff', border: '2px solid rgba(255,255,255,0.4)', borderRadius: '999px', padding: '6px 13px', fontWeight: 700, fontSize: '12.5px', whiteSpace: 'nowrap' };

export default function AppNav({ current, trialChip, onBuyPins, adminItems = [], onSignOut }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileRef = useRef(null);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onDown = (e) => { if (mobileRef.current && !mobileRef.current.contains(e.target)) setMobileOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mobileOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const signOut = onSignOut || (async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://flyatlast.com';
  });

  const items = [
    { label: 'Change name', action: () => navigate('/account/name') },
    { label: 'Change password', action: () => navigate('/reset-password') },
    { label: 'Change email', action: () => navigate('/account/email') },
    { label: 'Notifications', action: () => navigate('/account/notifications') },
    ...(onBuyPins ? [{ label: 'Buy more pins', action: onBuyPins }] : []),
    { label: 'Send feedback', action: () => { window.open(feedbackUrl(), '_blank', 'noopener'); } },
    { label: 'Contact support', action: () => { window.location.href = 'mailto:contactus@flyatlast.com'; } },
    { label: 'Delete account', action: () => navigate('/account/delete'), danger: true },
    ...adminItems,
  ];

  return (
    <div style={{ background: theme.structural, color: theme.cream, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'baseline', gap: '10px', cursor: 'pointer' }}>
        <div style={{ fontFamily: theme.fontDisplay, fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
          FlyAtlast
          <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: theme.action, display: 'inline-block' }} />
        </div>
        <span className="appnav-tagline" style={{ fontSize: '11.5px', color: 'rgba(253,246,236,0.55)', fontWeight: 600, letterSpacing: '0.02em' }}>
          Pin. Track. Fly.
        </span>
      </div>
      <div className="appnav-links" style={{ gap: '16px', alignItems: 'center', fontSize: '14px' }}>
        {current !== 'dashboard' && (
          <span onClick={() => navigate('/dashboard')} style={pill}>Dashboard</span>
        )}
        <span onClick={() => navigate('/payment-method')} style={current === 'payments' ? { ...pill, background: 'rgba(255,255,255,0.16)', borderColor: '#fff' } : pill}>Payments</span>
        <span
          onClick={() => navigate('/history')}
          style={current === 'history' ? { ...pill, background: 'rgba(255,255,255,0.16)', borderColor: '#fff' } : pill}
        >
          Trip History
        </span>
        {trialChip}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <span onClick={() => setMenuOpen(v => !v)} style={{ ...pill, display: 'flex', alignItems: 'center', gap: '6px' }}>
            Account
            <svg width="10" height="10" viewBox="0 0 10 10" style={{ transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
              <path d="M1.5 3 L5 6.5 L8.5 3" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {menuOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, background: '#fff', border: `2px solid ${theme.structural}`, borderRadius: '12px', minWidth: '190px', zIndex: 50, boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
              {items.map((item) => (
                <div
                  key={item.label}
                  onClick={() => { setMenuOpen(false); item.action(); }}
                  style={{ padding: '11px 16px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', color: item.danger ? '#B23B3B' : theme.structural, borderBottom: `1px solid ${theme.border}` }}
                >
                  {item.label}
                </div>
              ))}
              <div onClick={() => { setMenuOpen(false); signOut(); }} style={{ padding: '11px 16px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', color: theme.structural }}>
                Sign out
              </div>
            </div>
          )}
        </div>
        <div onClick={() => navigate('/pin')} style={{ background: theme.action, color: '#1a1a1a', padding: '8px 16px', borderRadius: '999px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
          <PinIcon size={16} color={theme.cream} />
          Pin a Trip
        </div>
      </div>
      {/* Phone layout: logo, Pin a Trip and one Menu button, so the bar stays
          one slim row instead of wrapping into big stacked pills. */}
      <div className="appnav-mobile" ref={mobileRef} style={{ position: 'relative', alignItems: 'center', gap: '10px' }}>
        <div onClick={() => navigate('/pin')} style={{ background: theme.action, color: '#1a1a1a', padding: '7px 13px', borderRadius: '999px', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
          <PinIcon size={14} color={theme.cream} />
          Pin a Trip
        </div>
        <span onClick={() => setMobileOpen(v => !v)} aria-label="Menu" style={{ ...pill, padding: '6px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Menu
          <svg width="10" height="10" viewBox="0 0 10 10" style={{ transform: mobileOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <path d="M1.5 3 L5 6.5 L8.5 3" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {mobileOpen && (
          <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, background: '#fff', border: `2px solid ${theme.structural}`, borderRadius: '12px', minWidth: '210px', maxHeight: '70vh', overflowY: 'auto', zIndex: 60, boxShadow: '0 4px 14px rgba(0,0,0,0.15)', color: theme.structural }}>
            {[
              ...(current !== 'dashboard' ? [{ label: 'Dashboard', action: () => navigate('/dashboard') }] : []),
              { label: 'Payments', action: () => navigate('/payment-method'), on: current === 'payments' },
              { label: 'Trip History', action: () => navigate('/history'), on: current === 'history' },
            ].map((item) => (
              <div key={item.label} onClick={() => { setMobileOpen(false); item.action(); }} style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 800, cursor: 'pointer', background: item.on ? '#EAF1F8' : '#fff', borderBottom: `1px solid ${theme.border}` }}>{item.label}</div>
            ))}
            {trialChip && <div style={{ padding: '10px 16px', borderBottom: `1px solid ${theme.border}` }}>{trialChip}</div>}
            {items.map((item) => (
              <div key={item.label} onClick={() => { setMobileOpen(false); item.action(); }} style={{ padding: '11px 16px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', color: item.danger ? '#B23B3B' : theme.structural, borderBottom: `1px solid ${theme.border}` }}>{item.label}</div>
            ))}
            <div onClick={() => { setMobileOpen(false); signOut(); }} style={{ padding: '11px 16px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}>Sign out</div>
          </div>
        )}
      </div>
      <style>{`
        .appnav-links { display: flex; }
        .appnav-mobile { display: none; }
        @media (max-width: 768px) {
          .appnav-links { display: none; }
          .appnav-mobile { display: flex; }
          .appnav-tagline { display: none; }
        }
      `}</style>
    </div>
  );
}
